/* eslint-disable react/prop-types */
import React, { useState } from 'react'
import { Row, Col, Container } from 'react-bootstrap'
import { FaTags, FaBoxes, FaImages, FaDatabase } from 'react-icons/fa'
import { useParams } from 'react-router-dom'
import {
  useAppSelector,
  useAppDispatch,
  useClassDistribution,
  useAnnotationStats,
} from '../../../store/hooks'
import { selectDatasetSources } from '../../../store/slices/datasetSlice'
import { setSelectedProjectIdAndClearPrediction, selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetContentMetadataQuery } from '../../../services/contentApi'
import '../../../styles/_dataset-overview.scss'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { ViewContainer, ScrollableContent } from './styles'
import { StatCard, ChartSection, ClassDistributionSection, DisplacementChart } from './components'
import ErrorDialog from '../../modals/ErrorDialog'
import { DisplacementStatsCard } from '../DisplacementChart/components'

const DatasetOverview: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const datasetSources = useAppSelector(selectDatasetSources)
  const [error, setError] = useState<string>('')
  const [showError, setShowError] = useState(false)
  const [displacementStats, setDisplacementStats] = React.useState<{
    contentData: any
    selectedNodes: Set<string>
    visualizationMode: string
    renderMode: string
    tagIdToNameAndColor: Record<string, { name: string; color: string }>
    onViewContent: () => void
    outlierReasons?: Map<number, string[]>
  } | null>(null)

  // Sync Redux store with URL parameter
  React.useEffect(() => {
    if (projectId && projectId !== selectedProjectId) {
      console.log('DatasetOverview: Syncing Redux store with URL project ID:', projectId)
      dispatch(setSelectedProjectIdAndClearPrediction(projectId))
      localStorage.setItem('selectedProjectId', projectId)
    }
  }, [projectId, selectedProjectId, dispatch])

  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  })

  const classDistribution = useClassDistribution(
    projectId || '',
    project?.datasetid || '',
    datasetSources
  )

  const {
    data: annoStats,
    refetch: refetchAnnoStats,
    isFetching: isFetchingStats,
  } = useAnnotationStats(projectId || '', project?.datasetid || '', datasetSources)

  const { data: metadata, refetch: refetchMetadata } = useGetContentMetadataQuery(
    {
      project_id: projectId || '',
    },
    { skip: !projectId }
  )

  React.useEffect(() => {
    if (projectId && project?.datasetid && (!annoStats || !metadata)) {
      const refetchData = async () => {
        try {
          await Promise.all([refetchAnnoStats(), refetchMetadata()])
        } catch (error: any) {
          setError(error?.data?.message || 'Error refetching dataset data')
          setShowError(true)
        }
      }
      const timeoutId = setTimeout(refetchData, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [
    projectId,
    project?.datasetid,
    datasetSources,
    refetchAnnoStats,
    refetchMetadata,
    annoStats,
    metadata,
  ])

  const totalImages =
    (annoStats?.statistics?.total_annotated_images || 0) +
    (annoStats?.statistics?.total_unannotated_images || 0)

  const totalClassWithoutUnknown = annoStats?.statistics?.annotations_per_class
    ? Object.keys(annoStats.statistics.annotations_per_class || {}).filter(
      (key) => key !== 'Unknown' && key !== 'null'
    )
    : []

  const classWithoutUnknown = classDistribution
    .filter((item) => item.name !== 'Unknown' && item.name !== 'null')
    .map((item) => ({
      ...item,
      balance: [item.balance], // Convert balance string to array
    }))

  // Bar chart data preparation
  const barChartData = React.useMemo(
    () => ({
      labels: ['Distribution'],
      datasets: classDistribution.map((item) => ({
        label: item.name,
        data: [item.value],
        backgroundColor: item.color,
        borderWidth: 1,
        borderColor: item.color,
        maxBarThickness: 30,
        minBarLength: 2,
      })),
    }),
    [classDistribution]
  )

  // Pie chart data preparation
  const pieChartData = React.useMemo(
    () => ({
      labels: classDistribution.map((item) => item.name),
      datasets: [
        {
          data: classDistribution.map((item) => item.value),
          backgroundColor: classDistribution.map((item) => item.color),
          barThickness: 24,
          maxBarThickness: 24,
          minBarLength: 2,
          label: '',
        },
      ],
    }),
    [classDistribution]
  )

  const barChartOptions = React.useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(255, 255, 255, 0.1)',
          },
          ticks: {
            color: '#ffffff',
          },
        },
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: '#ffffff',
          },
          offset: true,
          barPercentage: 1,
          categoryPercentage: 0.98,
        },
      },
      plugins: {
        legend: {
          display: true,
          position: 'top' as const,
          labels: {
            color: '#ffffff',
            padding: 20,
            usePointStyle: false,
          },
        },
      },
      layout: {
        padding: {
          left: 15,
          right: 15,
        },
      },
    }),
    []
  )

  const pieChartOptions = React.useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(255, 255, 255, 0.1)',
          },
          ticks: {
            color: '#ffffff',
          },
        },
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: '#ffffff',
          },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: 'top' as const,
          labels: {
            color: '#ffffff',
          },
        },
      },
    }),
    []
  )

  // Dataset Analytics chart data
  const datasetChartData = React.useMemo(
    () => ({
      labels: metadata?.metadata?.datasource || [],
      datasets: [
        {
          label: 'Images',
          data:
            metadata?.metadata?.datasource?.map((source: string) => {
              return annoStats?.statistics?.datasource_distributions?.[source]?.images || 0
            }) || [],
          backgroundColor: 'rgba(13, 202, 240, 0.7)',
          borderColor: 'rgba(13, 202, 240, 1)',
          borderWidth: 1,
        },
        {
          label: 'Annotations',
          data:
            metadata?.metadata?.datasource?.map((source: string) => {
              return annoStats?.statistics?.datasource_distributions?.[source]?.annotations || 0
            }) || [],
          backgroundColor: 'rgba(25, 135, 84, 0.7)',
          borderColor: 'rgba(25, 135, 84, 1)',
          borderWidth: 1,
        },
      ],
    }),
    [metadata?.metadata?.datasource, annoStats?.statistics?.datasource_distributions]
  )

  const isLoadingStats = !annoStats || !annoStats.statistics

  return (
    <ViewContainer>
      <ScrollableContent>
        <Row className="justify-content-center g-4 mb-4">
          <Col xs={12} md={3}>
            <StatCard
              variant="success"
              icon={<FaDatabase />}
              value={(metadata?.metadata?.datasource as Array<string>)?.length || 0}
              label="Datasets"
              isLoading={metadata?.metadata === undefined}
            />
          </Col>
          <Col xs={12} md={3}>
            <StatCard
              variant="primary"
              icon={<FaTags />}
              value={totalClassWithoutUnknown.length}
              label="Classes"
              isLoading={
                isLoadingStats ||
                isFetchingStats ||
                annoStats?.statistics?.annotations_per_class === undefined
              }
            />
          </Col>
          <Col xs={12} md={3}>
            <StatCard
              variant="info"
              icon={<FaImages />}
              value={totalImages}
              label="Images"
              isLoading={
                isLoadingStats ||
                isFetchingStats ||
                (annoStats?.statistics?.total_unannotated_images === undefined &&
                  annoStats?.statistics?.total_annotated_images === undefined)
              }
            />
          </Col>
          <Col xs={12} md={3}>
            <StatCard
              variant="warning"
              icon={<FaBoxes />}
              value={annoStats?.statistics?.total_annotations || 0}
              label="Annotations"
              isLoading={
                isLoadingStats ||
                isFetchingStats ||
                annoStats?.statistics?.total_annotations === undefined
              }
            />
          </Col>
        </Row>

        <Container fluid className="px-0">
          <Row>
            <Col xs={12} lg={4}>
              <ChartSection
                title="Dataset Analytics"
                data={datasetChartData}
                options={barChartOptions}
                isLoading={isLoadingStats || isFetchingStats || !metadata?.metadata}
              />
            </Col>
            <Col xs={12} lg={8}>
              <ClassDistributionSection
                classDistribution={classWithoutUnknown}
                isLoading={isLoadingStats || isFetchingStats}
                totalImages={totalImages}
                barChartData={barChartData}
                barChartOptions={barChartOptions}
                pieChartData={pieChartData}
                pieChartOptions={pieChartOptions}
              />
            </Col>
          </Row>

          <Row className="mt-4">
            <Col
              xs={12}
              lg={
                displacementStats?.selectedNodes && displacementStats.selectedNodes.size > 0
                  ? 8
                  : 12
              }
            >
              <DisplacementChart
                isLoading={isLoadingStats || isFetchingStats}
                onStatsUpdate={setDisplacementStats}
              />
            </Col>
            {displacementStats?.selectedNodes && displacementStats.selectedNodes.size > 0 && (
              <Col xs={12} lg={4}>
                <DisplacementStatsCard
                  isLoading={isLoadingStats || isFetchingStats}
                  contentData={displacementStats?.contentData || null}
                  selectedNodes={displacementStats?.selectedNodes || new Set()}
                  visualizationMode={displacementStats?.visualizationMode || 'default'}
                  renderMode={displacementStats?.renderMode || 'full'}
                  tagIdToNameAndColor={displacementStats?.tagIdToNameAndColor || {}}
                  onViewContent={displacementStats?.onViewContent}
                  outlierReasons={displacementStats?.outlierReasons}
                />
              </Col>
            )}
          </Row>
        </Container>
      </ScrollableContent>

      <ErrorDialog
        show={showError}
        onHide={() => setShowError(false)}
        message={error}
        title="Data Fetch Error"
      />
    </ViewContainer>
  )
}

export default DatasetOverview
